import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  inject,
  signal,
} from '@angular/core';
import { SplitterModule } from '@progress/kendo-angular-layout';

/**
 * Two side-by-side panels with a draggable divider between them — the app's wrapper around the Kendo
 * Splitter, so features never reach for `kendo-splitter` themselves. Project the panels with the
 * `splitFirst` / `splitSecond` attributes:
 *
 * ```html
 * <app-split-view [(firstSize)]="asideWidth" firstMin="240px" firstMax="520px">
 *   <aside splitFirst>…</aside>
 *   <section splitSecond>…</section>
 * </app-split-view>
 * ```
 *
 * The divider stands in for the gap between the panels: a transparent lane that shows a grip on hover.
 * Below `stackBelow` the split turns vertical, so a narrow screen gets the panels one above the other
 * (the host still owns the height — the splitter fills it).
 */
@Component({
  selector: 'app-split-view',
  standalone: true,
  imports: [SplitterModule],
  template: `
    <!-- Both panes are static: content projection matches splitFirst/splitSecond against the element,
         so a pane behind an @if would never reach the splitter's slot. The second pane is hidden
         through Kendo's own collapsed input instead. -->
    <kendo-splitter
      class="sv-splitter"
      [class.sv-single]="!secondVisible"
      [orientation]="orientation()"
    >
      <kendo-splitter-pane
        class="sv-pane"
        [scrollable]="false"
        [size]="firstSize"
        (sizeChange)="firstSizeChange.emit($event)"
        [min]="firstMin"
        [max]="firstMax"
      >
        <ng-content select="[splitFirst]"></ng-content>
      </kendo-splitter-pane>
      <kendo-splitter-pane
        class="sv-pane"
        [scrollable]="false"
        [size]="secondSize"
        (sizeChange)="secondSizeChange.emit($event)"
        [min]="secondMin"
        [max]="secondMax"
        [collapsed]="!secondVisible"
      >
        <ng-content select="[splitSecond]"></ng-content>
      </kendo-splitter-pane>
    </kendo-splitter>
  `,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
        min-width: 0;
        min-height: 0;
      }

      .sv-splitter {
        height: 100%;
        border: 0;
        background: transparent;
        overflow: visible;
      }

      .sv-pane {
        min-width: 0;
        min-height: 0;
        background: transparent;
      }

      .sv-pane > * {
        height: 100%;
      }

      .sv-splitter ::ng-deep .k-splitbar {
        position: relative;
        flex: 0 0 10px;
        background: transparent;
        border: 0;
      }

      /* The grip is always on screen so the divider reads as draggable without a hover hunt. */
      .sv-splitter ::ng-deep .k-splitbar::before {
        content: '';
        position: absolute;
        inset: 24px 9px;
        border-radius: 999px;
        background: var(--border-default, #e2e8f0);
        transition: background 0.15s ease;
      }

      .sv-splitter ::ng-deep .k-splitbar:hover::before,
      .sv-splitter ::ng-deep .k-splitbar.k-focus::before,
      .sv-splitter ::ng-deep .k-splitbar:active::before {
        background: var(--h4c-bright-blue, #27aae1);
      }

      .sv-splitter ::ng-deep .k-splitbar .k-resize-handle {
        display: none;
      }

      .sv-splitter ::ng-deep .k-splitbar-horizontal {
        cursor: col-resize;
      }

      .sv-splitter ::ng-deep .k-splitbar-vertical {
        flex: 0 0 16px;
        cursor: row-resize;
      }

      .sv-splitter ::ng-deep .k-splitbar-vertical::before {
        inset: 7px 24px;
      }

      .sv-splitter.sv-single ::ng-deep .k-splitbar {
        display: none;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SplitViewComponent {
  /**
   * Size of a panel, in px or %. Set exactly ONE of the two — an empty size means "take what is left",
   * and the Splitter needs one such pane. Both are two-way bindable so a drag survives a re-render.
   */
  @Input() firstSize = '';
  @Output() firstSizeChange = new EventEmitter<string>();
  @Input() secondSize = '';
  @Output() secondSizeChange = new EventEmitter<string>();

  @Input() firstMin = '200px';
  @Input() firstMax = '';
  @Input() secondMin = '200px';
  @Input() secondMax = '';

  /** Drop the second panel and the divider — the first one then fills the host on its own. */
  @Input() secondVisible = true;

  /** Viewport width at or below which the panels stack instead of splitting. 0 keeps them side by side. */
  @Input() set stackBelow(value: number) {
    this.stackAt = value;
    this.orientation.set(this.orientationForViewport());
  }

  private stackAt = 1200;

  readonly orientation = signal<'horizontal' | 'vertical'>(this.orientationForViewport());

  private orientationForViewport(): 'horizontal' | 'vertical' {
    return this.stackAt > 0 && window.innerWidth <= this.stackAt ? 'vertical' : 'horizontal';
  }

  private readonly onWindowResize = (): void => {
    this.orientation.set(this.orientationForViewport());
  };

  constructor() {
    window.addEventListener('resize', this.onWindowResize);
    inject(DestroyRef).onDestroy(() => window.removeEventListener('resize', this.onWindowResize));
  }
}
